import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { createDraftVersion, updateDraftVersion } from "../lib/api-client";
import { useBuilderStore } from "./useBuilderStore";
import {
  drainAutosaveTasks,
  enqueueAutosaveTask,
  hasQueuedAutosaveTasksSync,
  requeueAutosaveTasks,
  type AutosaveTask,
} from "../lib/offlineQueue";

const AUTOSAVE_DEBOUNCE_MS = 1400;

async function scheduleAutosaveSync(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    const syncManager = (registration as ServiceWorkerRegistration & {
      sync?: { register(tag: string): Promise<void> };
    }).sync;
    if (syncManager) {
      await syncManager.register("autosave-sync");
    }
  } catch {
    // Ignore unsupported browsers or registration failures.
  }
}

export function useAutoSave(): void {
  const schema = useBuilderStore((state) => state.schema);
  const dirty = useBuilderStore((state) => state.dirty);
  const formId = useBuilderStore((state) => state.formId);
  const draftVersionId = useBuilderStore((state) => state.draftVersionId);
  const setSaving = useBuilderStore((state) => state.setSaving);
  const markSaved = useBuilderStore((state) => state.markSaved);
  const loadSchema = useBuilderStore((state) => state.loadSchema);

  const isSavingRef = useRef(false);
  const savedAtVersionRef = useRef<number>(0);
  const flushInFlightRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const getSchemaVersion = useBuilderStore((state) => state.getSchemaVersion);

  const createDraft = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error("formId is not set");
      return createDraftVersion(formId, schema);
    },
    onMutate() {
      setSaving(true);
      isSavingRef.current = true;
      savedAtVersionRef.current = getSchemaVersion();
    },
    onSuccess(version) {
      loadSchema({
        id: version.id,
        formId: version.formId,
        versionId: version.id,
        schema: version.schema,
        logic: version.logic,
        settings: {},
        updatedAt: version.updatedAt,
      });
      markSaved(savedAtVersionRef.current);
      isSavingRef.current = false;
    },
    onError() {
      setSaving(false);
      isSavingRef.current = false;
    },
  });

  const saveDraft = useMutation({
    mutationFn: async (versionId: string) => {
      try {
        return await updateDraftVersion(versionId, schema);
      } catch (error) {
        // If the version is already published, create a new draft version
        if (error instanceof Error && (error.message.includes("400") || (error as { status?: number }).status === 400)) {
          if (!formId) throw new Error("formId is not set");
          console.log("[AutoSave] Version already published, creating new draft version...");
          const newVersion = await createDraftVersion(formId, schema);
          // Update the store with the new version ID
          loadSchema({
            id: newVersion.id,
            formId: newVersion.formId,
            versionId: newVersion.id,
            schema: newVersion.schema,
            logic: newVersion.logic,
            settings: {},
            updatedAt: newVersion.updatedAt,
          });
          return newVersion;
        }
        throw error;
      }
    },
    onMutate() {
      setSaving(true);
      isSavingRef.current = true;
      savedAtVersionRef.current = getSchemaVersion();
    },
    onSuccess() {
      markSaved(savedAtVersionRef.current);
      isSavingRef.current = false;
    },
    onError() {
      setSaving(false);
      isSavingRef.current = false;
    },
  });

  const flushOfflineQueue = useCallback(async () => {
    if (flushInFlightRef.current) {
      return;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return;
    }

    // Cancel any in-flight operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this flush operation
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    flushInFlightRef.current = true;
    try {
      const tasks = await drainAutosaveTasks();
      if (tasks.length === 0) {
        return;
      }

      const failedTasks: AutosaveTask[] = [];

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];

        // Check if operation was aborted
        if (abortController.signal.aborted) {
          await requeueAutosaveTasks(tasks.slice(i));
          return;
        }

        isSavingRef.current = true;
        setSaving(true);

        let retryAttempt = 0;
        let success = false;

        // Retry logic with exponential backoff
        while (retryAttempt < 5 && !success && !abortController.signal.aborted) {
          try {
            if (task.type === "create") {
              const version = await createDraftVersion(task.formId, task.schema);
              // Check again after async operation
              if (abortController.signal.aborted) {
                failedTasks.push(...tasks.slice(i));
                await requeueAutosaveTasks(failedTasks);
                return;
              }
              loadSchema({
                id: version.id,
                formId: version.formId,
                versionId: version.id,
                schema: version.schema,
                logic: version.logic,
                settings: {},
                updatedAt: version.updatedAt,
              });
              markSaved();
              success = true;
            } else {
              await updateDraftVersion(task.versionId, task.schema);
              // Check again after async operation
              if (abortController.signal.aborted) {
                failedTasks.push(...tasks.slice(i));
                await requeueAutosaveTasks(failedTasks);
                return;
              }
              markSaved();
              success = true;
            }
          } catch (_error) {
            retryAttempt++;
            if (retryAttempt < 5 && !abortController.signal.aborted) {
              // Exponential backoff
              const delay = Math.min(1000 * Math.pow(2, retryAttempt - 1), 30000);
              await new Promise((resolve) => setTimeout(resolve, delay));
            } else {
              // Max retries reached or aborted
              failedTasks.push(task);
              if (i < tasks.length - 1) {
                failedTasks.push(...tasks.slice(i + 1));
              }
              await requeueAutosaveTasks(failedTasks, retryAttempt - 1);
              break;
            }
          }
        }

        isSavingRef.current = false;
        setSaving(false);
      }
    } finally {
      flushInFlightRef.current = false;
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [loadSchema, markSaved, setSaving]);

  useEffect(() => {
    if (!dirty || !formId) {
      return;
    }

    // Cancel any pending autosave
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const timeout = setTimeout(() => {
      // Check if component unmounted or operation was aborted
      if (abortController.signal.aborted || isSavingRef.current) {
        return;
      }

      const online = typeof navigator === "undefined" ? true : navigator.onLine;
      if (!online) {
        if (!draftVersionId) {
          void enqueueAutosaveTask({ type: "create", formId: formId!, schema } as Omit<AutosaveTask, "id" | "createdAt">);
        } else {
          void enqueueAutosaveTask({ type: "update", versionId: draftVersionId, schema } as Omit<AutosaveTask, "id" | "createdAt">);
        }
        void scheduleAutosaveSync();
        setSaving(hasQueuedAutosaveTasksSync());
        return;
      }

      // Check again before starting mutation
      if (abortController.signal.aborted) {
        return;
      }

      if (!draftVersionId) {
        createDraft.mutate();
      } else {
        saveDraft.mutate(draftVersionId);
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      abortController.abort();
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    };
  }, [dirty, schema, formId, draftVersionId, createDraft, saveDraft, setSaving]);

  useEffect(() => {
    const handleOnline = () => {
      void flushOfflineQueue();
    };
    window.addEventListener("online", handleOnline);
    void flushOfflineQueue();
    return () => {
      window.removeEventListener("online", handleOnline);
      // Cancel any in-flight flush operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [flushOfflineQueue]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) {
      return;
    }
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "FLUSH_AUTOSAVE_QUEUE") {
        void flushOfflineQueue();
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage as unknown as EventListener);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage as unknown as EventListener);
      // Cancel any in-flight flush operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [flushOfflineQueue]);

  useEffect(() => {
    if (hasQueuedAutosaveTasksSync()) {
      setSaving(true);
    }
  }, [setSaving]);
}
